/**
 * DiscoveryScene - Exploration and scanning gameplay
 * Players explore microscope slides to discover and catalog specimens
 */

import Phaser from 'phaser';
import { MicroscopeSystem, ScannerSystem } from '@/game/systems';
import type { Specimen, ScanCompleteEvent } from '@/game/systems';
import { MicroscopeHUD, ScannerPanel, EvidenceCards } from '@/ui/components';
import { CellAnimationSystem, NonLivingGenerator, CellRenderer } from '@/procedural/graphics';
import { getLevel, getChapterForLevel, CHAPTERS } from '@/data';
import { getOrganismTemplate, getNonLivingTemplate, isLivingSpecimen } from '@/data';
import { markLevelComplete } from './MenuScene';
import type { Level, LevelObjective, EvidenceCard, OrganismTemplate, ScanResult } from '@/types';

export interface DiscoverySceneConfig {
  levelId: string;
}

interface ObjectiveProgress {
  objective: LevelObjective;
  current: number;
  target: number;
  complete: boolean;
}

interface PlacedSpecimen {
  id: string;
  templateId: string;
  specimen: Specimen;
  container: Phaser.GameObjects.Container;
  scanned: boolean;
  cataloged: boolean;
}

/**
 * DiscoveryScene handles exploration gameplay
 * - Microscope navigation
 * - Specimen scanning
 * - Discovery cataloging
 * - Objective tracking
 */
export class DiscoveryScene extends Phaser.Scene {
  private levelId!: string;
  private level!: Level;

  /** Systems */
  private microscopeSystem!: MicroscopeSystem;
  private scannerSystem!: ScannerSystem;
  private animationSystem!: CellAnimationSystem;
  private nonLivingGenerator!: NonLivingGenerator;

  /** UI Components */
  private microscopeHUD!: MicroscopeHUD;
  private scannerPanel!: ScannerPanel;
  private evidenceCards!: EvidenceCards;

  /** Game objects */
  private slideContainer!: Phaser.GameObjects.Container;
  private placedSpecimens: Map<string, PlacedSpecimen> = new Map();

  /** Game state */
  private objectives: ObjectiveProgress[] = [];
  private discoveredCells: Set<string> = new Set();
  private catalogedCells: Set<string> = new Set();
  private identifiedNonLiving: Set<string> = new Set();
  private collectedEvidence: EvidenceCard[] = [];
  private classifiedCells: Map<string, 'living' | 'non-living'> = new Map();

  /** UI elements */
  private objectivePanel!: Phaser.GameObjects.Container;
  private journalButton!: Phaser.GameObjects.Container;
  private completionOverlay?: Phaser.GameObjects.Container;
  private scanPopup?: Phaser.GameObjects.Container;
  private tutorialOverlay?: Phaser.GameObjects.Container;
  private journalOverlay?: Phaser.GameObjects.Container;

  /** Pan state */
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private slideStartX = 0;
  private slideStartY = 0;

  /** Floating background orbs */
  private floatingOrbs: Phaser.GameObjects.Container[] = [];

  constructor() {
    super({ key: 'DiscoveryScene' });
  }

  init(data: DiscoverySceneConfig): void {
    this.levelId = data.levelId;

    // Reset state
    this.placedSpecimens.clear();
    this.objectives = [];
    this.discoveredCells.clear();
    this.catalogedCells.clear();
    this.identifiedNonLiving.clear();
    this.collectedEvidence = [];
    this.classifiedCells.clear();
  }

  create(): void {
    // Load level data
    const level = getLevel(this.levelId);
    if (!level) {
      console.error(`Level not found: ${this.levelId}`);
      this.scene.start('MenuScene');
      return;
    }
    this.level = level;

    // Create slide background
    this.createSlideBackground();

    // Initialize systems
    this.initializeSystems();

    // Place specimens
    this.placeSpecimens();

    // Create UI
    this.createUI();

    // Initialize objectives
    this.initializeObjectives();

    // Setup event listeners
    this.setupEventListeners();

    // Start microscope at 40x
    this.microscopeSystem.setZoomLevel(40);

    // Activate scanner so specimens can be scanned
    this.scannerSystem.activate();

    // Show tutorial on first level
    if (this.levelId === '1-1') {
      this.showTutorialHint();
    }

    // Emit level start event
    this.events.emit('level:start', { levelId: this.levelId, level: this.level });
  }

  override update(_time: number, delta: number): void {
    this.animationSystem.update(delta);
  }

  /**
   * Create the microscope slide background with deep bioluminescent gradients
   */
  private createSlideBackground(): void {
    const { width, height } = this.cameras.main;
    const slideWidth = width * 3;
    const slideHeight = height * 3;

    this.slideContainer = this.add.container(0, 0);

    // Base deep ocean background
    const slideBg = this.add.graphics();
    slideBg.fillStyle(0x040810, 1);
    slideBg.fillRect(-slideWidth / 2, -slideHeight / 2, slideWidth, slideHeight);
    this.slideContainer.add(slideBg);

    // Deep radial gradient from center (like light from microscope)
    const radialGradient = this.add.graphics();
    const centerX = 0;
    const centerY = 0;
    const maxRadius = Math.max(slideWidth, slideHeight) * 0.7;

    // Create multiple radial layers for smooth gradient
    for (let i = 40; i >= 0; i--) {
      const radius = (i / 40) * maxRadius;
      const alpha = 0.015 * (1 - i / 40);
      radialGradient.fillStyle(0x0d2847, alpha);
      radialGradient.fillCircle(centerX, centerY, radius);
    }
    this.slideContainer.add(radialGradient);

    // Add bioluminescent glow spots (like deep sea creatures)
    const glowSpots = this.add.graphics();
    const glowPositions = [
      { x: -slideWidth * 0.25, y: -slideHeight * 0.2, color: 0x00d4ff, size: 300 },
      { x: slideWidth * 0.3, y: slideHeight * 0.15, color: 0x00ffc8, size: 250 },
      { x: -slideWidth * 0.1, y: slideHeight * 0.3, color: 0xa855f7, size: 200 },
      { x: slideWidth * 0.2, y: -slideHeight * 0.25, color: 0x00ff88, size: 180 },
      { x: -slideWidth * 0.35, y: slideHeight * 0.1, color: 0x00d4ff, size: 220 },
    ];

    glowPositions.forEach(glow => {
      for (let i = 15; i >= 0; i--) {
        const radius = (i / 15) * glow.size;
        const alpha = 0.008 * (1 - i / 15);
        glowSpots.fillStyle(glow.color, alpha);
        glowSpots.fillCircle(glow.x, glow.y, radius);
      }
    });
    this.slideContainer.add(glowSpots);

    // Vertical gradient overlay (darker at top, lighter in middle, darker at bottom)
    const verticalGradient = this.add.graphics();
    const gradientSteps = 30;
    for (let i = 0; i < gradientSteps; i++) {
      const yPos = (-slideHeight / 2) + (slideHeight / gradientSteps) * i;
      const heightStep = slideHeight / gradientSteps;

      // Create U-shaped brightness (darker at top and bottom)
      const normalizedPos = (i / gradientSteps) * 2 - 1; // -1 to 1
      const brightness = 1 - Math.abs(normalizedPos) * 0.8;
      const alpha = 0.03 * brightness;

      verticalGradient.fillStyle(0x1a3a5c, alpha);
      verticalGradient.fillRect(-slideWidth / 2, yPos, slideWidth, heightStep);
    }
    this.slideContainer.add(verticalGradient);

    // Add subtle noise texture (tiny particles)
    const noiseTexture = this.add.graphics();
    for (let i = 0; i < 500; i++) {
      const x = (Math.random() - 0.5) * slideWidth;
      const y = (Math.random() - 0.5) * slideHeight;
      const alpha = Math.random() * 0.08 + 0.02;
      noiseTexture.fillStyle(0x4a90a4, alpha);
      noiseTexture.fillCircle(x, y, Math.random() * 1 + 0.3);
    }
    this.slideContainer.add(noiseTexture);

    // Add floating bioluminescent particles (larger, more visible)
    const particles = this.add.graphics();
    for (let i = 0; i < 80; i++) {
      const x = (Math.random() - 0.5) * slideWidth;
      const y = (Math.random() - 0.5) * slideHeight;
      const size = Math.random() * 3 + 1;

      // Particle glow
      particles.fillStyle(0x00d4ff, 0.1);
      particles.fillCircle(x, y, size * 2.5);
      // Particle core
      particles.fillStyle(0x00ffc8, 0.25);
      particles.fillCircle(x, y, size);
    }
    this.slideContainer.add(particles);

    // Soft vignette effect (radial darkness at edges)
    const vignette = this.add.graphics();

    // Corner darkening
    const vignetteCorners = [
      { x: -slideWidth / 2, y: -slideHeight / 2 },
      { x: slideWidth / 2, y: -slideHeight / 2 },
      { x: -slideWidth / 2, y: slideHeight / 2 },
      { x: slideWidth / 2, y: slideHeight / 2 },
    ];

    vignetteCorners.forEach(corner => {
      for (let i = 10; i >= 0; i--) {
        const radius = (i / 10) * 400;
        const alpha = 0.08 * (1 - i / 10);
        vignette.fillStyle(0x000000, alpha);
        vignette.fillCircle(corner.x, corner.y, radius);
      }
    });

    // Edge darkening (softer)
    vignette.fillStyle(0x000000, 0.2);
    vignette.fillRect(-slideWidth / 2, -slideHeight / 2, 60, slideHeight);
    vignette.fillRect(slideWidth / 2 - 60, -slideHeight / 2, 60, slideHeight);
    vignette.fillRect(-slideWidth / 2, -slideHeight / 2, slideWidth, 60);
    vignette.fillRect(-slideWidth / 2, slideHeight / 2 - 60, slideWidth, 60);
    this.slideContainer.add(vignette);

    // Create animated floating orbs (deep water effect)
    this.createFloatingOrbs(slideWidth, slideHeight);

    // Center the slide
    this.slideContainer.setPosition(width / 2, height / 2);
  }

  /**
   * Create animated floating blurred orbs for deep water effect
   */
  private createFloatingOrbs(slideWidth: number, slideHeight: number): void {
    // Define orb configurations - subtle deep blues, visible but not overwhelming
    const orbConfigs = [
      { x: -slideWidth * 0.12, y: -slideHeight * 0.08, size: 320, color: 0x0c3d5e, alpha: 0.28 },
      { x: slideWidth * 0.1, y: slideHeight * 0.05, size: 380, color: 0x0f4a72, alpha: 0.22 },
      { x: -slideWidth * 0.05, y: slideHeight * 0.1, size: 260, color: 0x1a5a8a, alpha: 0.25 },
      { x: slideWidth * 0.15, y: -slideHeight * 0.06, size: 340, color: 0x0d4060, alpha: 0.3 },
      { x: -slideWidth * 0.15, y: slideHeight * 0.02, size: 300, color: 0x0b3550, alpha: 0.24 },
      { x: slideWidth * 0.05, y: -slideHeight * 0.12, size: 240, color: 0x104570, alpha: 0.26 },
      { x: -slideWidth * 0.08, y: -slideHeight * 0.15, size: 360, color: 0x0a2d45, alpha: 0.22 },
      { x: slideWidth * 0.18, y: slideHeight * 0.12, size: 280, color: 0x155080, alpha: 0.28 },
      { x: 0, y: slideHeight * 0.06, size: 420, color: 0x0c3858, alpha: 0.2 },
      { x: -slideWidth * 0.18, y: -slideHeight * 0.04, size: 330, color: 0x0e4268, alpha: 0.23 },
      { x: slideWidth * 0.08, y: slideHeight * 0.15, size: 290, color: 0x0d3d5c, alpha: 0.25 },
      { x: -slideWidth * 0.02, y: -slideHeight * 0.1, size: 350, color: 0x124a75, alpha: 0.22 },
    ];

    this.floatingOrbs = [];

    orbConfigs.forEach((config, index) => {
      // Create container for this orb
      const orbContainer = this.add.container(config.x, config.y);

      // Create extremely blurred orb using many concentric circles
      const orbGraphics = this.add.graphics();

      // Draw many layers for ultra-soft blur effect
      const layers = 45;
      for (let i = layers; i >= 0; i--) {
        const ratio = i / layers;
        const radius = config.size * ratio;
        // Subtle alpha - softer falloff
        const layerAlpha = config.alpha * Math.pow(1 - ratio, 0.8) * 0.7;
        orbGraphics.fillStyle(config.color, layerAlpha);
        orbGraphics.fillCircle(0, 0, radius);
      }

      // Add subtle brighter core
      for (let i = 18; i >= 0; i--) {
        const ratio = i / 18;
        const radius = config.size * 0.35 * ratio;
        const coreAlpha = config.alpha * 0.35 * (1 - ratio);
        // Slightly lighter shade for core
        const coreColor = Phaser.Display.Color.ValueToColor(config.color);
        coreColor.lighten(18);
        orbGraphics.fillStyle(coreColor.color, coreAlpha);
        orbGraphics.fillCircle(0, 0, radius);
      }

      orbContainer.add(orbGraphics);
      this.slideContainer.add(orbContainer);
      this.floatingOrbs.push(orbContainer);

      // Create organic floating animation with larger movements
      const duration = 6000 + Math.random() * 4000;
      const delay = Math.random() * 1000;
      const driftX = 60 + Math.random() * 80;
      const driftY = 50 + Math.random() * 70;

      // Horizontal drift
      this.tweens.add({
        targets: orbContainer,
        x: config.x + (Math.random() > 0.5 ? driftX : -driftX),
        duration: duration,
        delay: delay,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      // Vertical drift (slightly different timing for organic feel)
      this.tweens.add({
        targets: orbContainer,
        y: config.y + (Math.random() > 0.5 ? driftY : -driftY),
        duration: duration * 1.2,
        delay: delay + 500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      // Subtle scale breathing effect
      this.tweens.add({
        targets: orbContainer,
        scaleX: 1 + Math.random() * 0.08,
        scaleY: 1 + Math.random() * 0.06,
        duration: duration * 0.8,
        delay: delay + index * 200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      // Very subtle alpha pulsing
      this.tweens.add({
        targets: orbGraphics,
        alpha: 0.85 + Math.random() * 0.15,
        duration: duration * 0.6,
        delay: delay,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    });
  }

  /**
   * Initialize game systems
   */
  private initializeSystems(): void {
    // Microscope system for zoom/pan
    this.microscopeSystem = new MicroscopeSystem(this, {
      initialZoomIndex: 2, // Start at 40x
      touchEnabled: true,
    });

    // Scanner system for analyzing specimens
    this.scannerSystem = new ScannerSystem(this, {
      scanDuration: 3000,
      scanRange: 80,
    });

    // Animation system for living cells
    this.animationSystem = new CellAnimationSystem(this, {
      pulseAmplitude: 0.03,
      pulseSpeed: 0.4,
      driftRadius: 3,
      driftSpeed: 0.3,
      cytoplasmFlow: true,
      speedMultiplier: 1,
    });
    this.animationSystem.start();

    // Non-living generator
    this.nonLivingGenerator = new NonLivingGenerator(this, Date.now());
  }

  /**
   * Place specimens on the slide
   */
  private placeSpecimens(): void {
    if (!this.level.specimens) return;

    const { width, height } = this.cameras.main;
    const spreadX = width * 1.5;
    const spreadY = height * 1.5;

    this.level.specimens.forEach((specimenId, index) => {
      // Generate position with some randomness
      const angle = (index / this.level.specimens!.length) * Math.PI * 2;
      const radius = 150 + Math.random() * 200;
      const x = Math.cos(angle) * radius + (Math.random() - 0.5) * spreadX * 0.3;
      const y = Math.sin(angle) * radius + (Math.random() - 0.5) * spreadY * 0.3;

      this.createSpecimen(specimenId, x, y, index);
    });
  }

  /**
   * Create a single specimen
   */
  private createSpecimen(templateId: string, x: number, y: number, index: number): void {
    const isLiving = isLivingSpecimen(templateId);
    const id = `${templateId}_${index}`;

    // Convert local coordinates to world coordinates
    const worldX = this.slideContainer.x + x;
    const worldY = this.slideContainer.y + y;

    let container: Phaser.GameObjects.Container;
    let size: number;

    // Check if this is an organism template (even if non-living like virus)
    const organismTemplate = getOrganismTemplate(templateId);

    if (organismTemplate) {
      // Create cell with animation system (for both living and tricky non-living like virus)
      const template = organismTemplate;

      size = template.cellConfig.size.min + Math.random() * (template.cellConfig.size.max - template.cellConfig.size.min);

      // Create animated cell at world position
      const cellId = `cell_${id}`;
      this.animationSystem.createAnimatedCell(template.cellConfig, Date.now() + index, cellId);
      this.animationSystem.positionCell(cellId, worldX, worldY);

      // Get the container from animation system
      const animatedCell = this.animationSystem.getCell(cellId);
      container = animatedCell?.result.container ?? this.add.container(worldX, worldY);

      // Determine cell count from template type
      const cellCount = template.type === 'unicellular' ? 'single' : 'multiple';

      // Build specimen data with world coordinates
      // Use isLiving from function to handle tricky specimens like virus (has cell structure but no life functions)
      const specimen: Specimen = {
        id,
        position: { x: worldX, y: worldY },
        isLiving,
        cellCount: isLiving ? cellCount : 'none',
        cellType: this.getCellTypeFromTemplate(template),
        lifeFunctions: this.convertLifeFunctions(template),
        bounds: new Phaser.Geom.Rectangle(worldX - size / 2, worldY - size / 2, size, size),
      };

      this.placedSpecimens.set(id, {
        id,
        templateId,
        specimen,
        container,
        scanned: false,
        cataloged: false,
      });

      this.scannerSystem.registerSpecimen(specimen);
    } else {
      // Create non-living object
      const template = getNonLivingTemplate(templateId);
      if (!template) {
        console.warn(`Unknown non-living template: ${templateId}`);
        return;
      }

      size = 40 + Math.random() * 40;

      const rendered = this.nonLivingGenerator.generate(template, {
        size,
        seed: Date.now() + index,
      });
      container = rendered.container;
      container.setPosition(worldX, worldY);

      // Build specimen data with world coordinates
      const specimen: Specimen = {
        id,
        position: { x: worldX, y: worldY },
        isLiving: false,
        cellCount: 'none',
        cellType: 'none',
        lifeFunctions: [],
        bounds: new Phaser.Geom.Rectangle(worldX - size / 2, worldY - size / 2, size, size),
      };

      this.placedSpecimens.set(id, {
        id,
        templateId,
        specimen,
        container,
        scanned: false,
        cataloged: false,
      });

      this.scannerSystem.registerSpecimen(specimen);
    }
  }

  /**
   * Get cell type from organism template
   */
  private getCellTypeFromTemplate(template: OrganismTemplate): Specimen['cellType'] {
    const cellType = template.cellConfig.cellType;
    if (cellType === 'bacteria') return 'prokaryotic';
    if (cellType === 'plant') return 'eukaryotic-plant';
    return 'eukaryotic';
  }

  /**
   * Convert LifeFunctionDisplay to LifeFunction array
   */
  private convertLifeFunctions(_template: OrganismTemplate): Specimen['lifeFunctions'] {
    // Since LifeFunctionDisplay doesn't match LifeFunction enum,
    // we return an empty array and let the scanner generate appropriate functions
    return [];
  }

  /**
   * Create UI elements
   */
  private createUI(): void {
    const { width, height } = this.cameras.main;

    // Back button (top left)
    this.createBackButton();

    // Microscope HUD (right side)
    this.microscopeHUD = new MicroscopeHUD(this, this.microscopeSystem, {
      x: width - 50,
      y: height / 2,
    });

    // Scanner panel (bottom left) - listens to events automatically
    this.scannerPanel = new ScannerPanel(this, this.scannerSystem, {
      x: 20,
      y: height - 210,
    });

    // Evidence cards (top left, below back button)
    this.evidenceCards = new EvidenceCards(this, {
      x: 20,
      y: 70,
      maxCards: 4,
    });

    // Objective panel (top center)
    this.createObjectivePanel();

    // Journal button (top right)
    this.createJournalButton();

    // Level title
    this.createLevelTitle();
  }

  /**
   * Create objective tracking panel
   */
  private createObjectivePanel(): void {
    const { width } = this.cameras.main;

    this.objectivePanel = this.add.container(width / 2, 60);
    this.objectivePanel.setDepth(1000);

    // Panel background
    const panelWidth = 300;
    const panelHeight = 80;
    const bg = this.add.graphics();
    bg.fillStyle(0x0a1628, 0.85);
    bg.fillRoundedRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 10);
    bg.lineStyle(1, 0x4299e1, 0.5);
    bg.strokeRoundedRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 10);

    // Title
    const title = this.add.text(0, -panelHeight / 2 + 15, 'OBJECTIVES', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '12px',
      color: '#90cdf4',
    }).setOrigin(0.5);

    this.objectivePanel.add([bg, title]);
  }

  /**
   * Create journal button
   */
  private createJournalButton(): void {
    const { width } = this.cameras.main;

    this.journalButton = this.add.container(width - 120, 40);
    this.journalButton.setDepth(1000);

    // Button background
    const bg = this.add.graphics();
    bg.fillStyle(0x1a365d, 0.9);
    bg.fillRoundedRect(-40, -20, 80, 40, 8);
    bg.lineStyle(2, 0x4299e1, 0.7);
    bg.strokeRoundedRect(-40, -20, 80, 40, 8);

    // Label
    const label = this.add.text(0, 0, 'Journal', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      color: '#e2e8f0',
    }).setOrigin(0.5);

    this.journalButton.add([bg, label]);
    this.journalButton.setSize(80, 40);
    this.journalButton.setInteractive({ useHandCursor: true });

    this.journalButton.on('pointerdown', () => this.openJournal());
    this.journalButton.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x2d4a6f, 0.9);
      bg.fillRoundedRect(-40, -20, 80, 40, 8);
      bg.lineStyle(2, 0x68d391, 0.9);
      bg.strokeRoundedRect(-40, -20, 80, 40, 8);
    });
    this.journalButton.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x1a365d, 0.9);
      bg.fillRoundedRect(-40, -20, 80, 40, 8);
      bg.lineStyle(2, 0x4299e1, 0.7);
      bg.strokeRoundedRect(-40, -20, 80, 40, 8);
    });
  }

  /**
   * Create level title display
   */
  private createLevelTitle(): void {
    const chapter = getChapterForLevel(this.levelId);
    const chapterTitle = chapter?.title ?? 'Unknown Chapter';

    const title = this.add.text(20, this.cameras.main.height - 25, `${chapterTitle} - ${this.level.name}`, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      color: '#a0aec0',
    });
    title.setDepth(1000);
  }

  /**
   * Create back button to return to menu
   */
  private createBackButton(): void {
    const container = this.add.container(20, 20);

    // Button background with glow
    const glow = this.add.graphics();
    glow.fillStyle(0x00d4ff, 0.1);
    glow.fillRoundedRect(-2, -2, 78, 32, 10);

    const bg = this.add.graphics();
    bg.fillStyle(0x0a1628, 0.9);
    bg.fillRoundedRect(0, 0, 74, 28, 8);
    bg.lineStyle(1, 0x00d4ff, 0.5);
    bg.strokeRoundedRect(0, 0, 74, 28, 8);

    // Arrow and text
    const text = this.add.text(37, 14, '← Back', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '12px',
      color: '#00d4ff',
    }).setOrigin(0.5);

    container.add([glow, bg, text]);
    container.setSize(74, 28);
    container.setInteractive({ useHandCursor: true });
    container.setDepth(1000);

    container.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x0a1628, 1);
      bg.fillRoundedRect(0, 0, 74, 28, 8);
      bg.lineStyle(2, 0x00d4ff, 0.8);
      bg.strokeRoundedRect(0, 0, 74, 28, 8);
      text.setColor('#00ffd4');
    });

    container.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x0a1628, 0.9);
      bg.fillRoundedRect(0, 0, 74, 28, 8);
      bg.lineStyle(1, 0x00d4ff, 0.5);
      bg.strokeRoundedRect(0, 0, 74, 28, 8);
      text.setColor('#00d4ff');
    });

    container.on('pointerdown', () => {
      this.scene.start('MenuScene');
    });
  }

  /**
   * Show tutorial hint overlay for first-time players
   */
  private showTutorialHint(): void {
    const { width, height } = this.cameras.main;

    this.tutorialOverlay = this.add.container(width / 2, height / 2);
    this.tutorialOverlay.setDepth(2000);

    // Semi-transparent backdrop
    const backdrop = this.add.graphics();
    backdrop.fillStyle(0x000000, 0.7);
    backdrop.fillRect(-width / 2, -height / 2, width, height);
    backdrop.setInteractive(new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height), Phaser.Geom.Rectangle.Contains);
    this.tutorialOverlay.add(backdrop);

    // Tutorial card
    const cardWidth = 480;
    const cardHeight = 380;

    // Card background with glow
    const cardGlow = this.add.graphics();
    for (let i = 4; i >= 0; i--) {
      cardGlow.fillStyle(0x00d4ff, 0.03 * (5 - i));
      cardGlow.fillRoundedRect(-cardWidth / 2 - i * 4, -cardHeight / 2 - i * 4, cardWidth + i * 8, cardHeight + i * 8, 16 + i * 2);
    }
    this.tutorialOverlay.add(cardGlow);

    const card = this.add.graphics();
    card.fillStyle(0x0a1628, 0.98);
    card.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 16);
    card.lineStyle(2, 0x00d4ff, 0.6);
    card.strokeRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 16);
    this.tutorialOverlay.add(card);

    // Title
    const title = this.add.text(0, -cardHeight / 2 + 35, 'Welcome to the Microscope Lab!', {
      fontFamily: 'Georgia, serif',
      fontSize: '22px',
      fontStyle: 'italic',
      color: '#00ffd4',
    }).setOrigin(0.5);
    title.setShadow(0, 0, '#00ffd4', 8, true, true);
    this.tutorialOverlay.add(title);

    // Instructions
    const instructions = [
      { icon: '🔬', text: 'Drag to pan around the microscope slide' },
      { icon: '🔍', text: 'Use the zoom slider on the right to magnify' },
      { icon: '👆', text: 'Click on specimens to scan and analyze them' },
      { icon: '📋', text: 'Collect evidence to identify cells vs non-living' },
      { icon: '✨', text: 'Complete objectives shown at the top' },
    ];

    instructions.forEach((inst, index) => {
      const y = -cardHeight / 2 + 90 + index * 42;

      // Icon
      const icon = this.add.text(-cardWidth / 2 + 40, y, inst.icon, {
        fontSize: '24px',
      }).setOrigin(0.5);
      this.tutorialOverlay!.add(icon);

      // Text
      const text = this.add.text(-cardWidth / 2 + 70, y, inst.text, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#e2e8f0',
      }).setOrigin(0, 0.5);
      this.tutorialOverlay!.add(text);
    });

    // "Got it!" button
    const btnWidth = 140;
    const btnHeight = 44;
    const btnY = cardHeight / 2 - 55;

    const btnContainer = this.add.container(0, btnY);

    const btnGlow = this.add.graphics();
    for (let i = 3; i >= 0; i--) {
      btnGlow.fillStyle(0x00ff88, 0.08 * (4 - i));
      btnGlow.fillRoundedRect(-btnWidth / 2 - i * 3, -btnHeight / 2 - i * 3, btnWidth + i * 6, btnHeight + i * 6, 10 + i);
    }
    btnContainer.add(btnGlow);

    const btnBg = this.add.graphics();
    btnBg.fillStyle(0x00ff88, 0.2);
    btnBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 10);
    btnBg.lineStyle(2, 0x00ff88, 0.8);
    btnBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 10);
    btnContainer.add(btnBg);

    const btnText = this.add.text(0, 0, "Got it!", {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '18px',
      fontStyle: 'bold',
      color: '#00ff88',
    }).setOrigin(0.5);
    btnContainer.add(btnText);

    // Make button interactive
    const hitArea = this.add.rectangle(0, 0, btnWidth, btnHeight, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    btnContainer.add(hitArea);

    hitArea.on('pointerover', () => {
      btnBg.clear();
      btnBg.fillStyle(0x00ff88, 0.35);
      btnBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 10);
      btnBg.lineStyle(2, 0x00ff88, 1);
      btnBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 10);
      btnText.setColor('#ffffff');
    });

    hitArea.on('pointerout', () => {
      btnBg.clear();
      btnBg.fillStyle(0x00ff88, 0.2);
      btnBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 10);
      btnBg.lineStyle(2, 0x00ff88, 0.8);
      btnBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 10);
      btnText.setColor('#00ff88');
    });

    hitArea.on('pointerdown', () => {
      this.closeTutorialHint();
    });

    this.tutorialOverlay.add(btnContainer);

    // Entrance animation
    this.tutorialOverlay.setAlpha(0);
    this.tutorialOverlay.setScale(0.9);
    this.tweens.add({
      targets: this.tutorialOverlay,
      alpha: 1,
      scale: 1,
      duration: 300,
      ease: 'Back.easeOut',
    });
  }

  /**
   * Close the tutorial hint overlay
   */
  private closeTutorialHint(): void {
    if (!this.tutorialOverlay) return;

    this.tweens.add({
      targets: this.tutorialOverlay,
      alpha: 0,
      scale: 0.9,
      duration: 200,
      ease: 'Power2',
      onComplete: () => {
        this.tutorialOverlay?.destroy();
        this.tutorialOverlay = undefined;
      },
    });
  }

  /**
   * Initialize objectives from level data
   */
  private initializeObjectives(): void {
    this.objectives = this.level.objectives.map(objective => ({
      objective,
      current: 0,
      target: this.getObjectiveTarget(objective),
      complete: false,
    }));

    this.updateObjectiveDisplay();
  }

  /**
   * Get target count for an objective
   */
  private getObjectiveTarget(objective: LevelObjective): number {
    return objective.count ?? objective.threshold ?? 1;
  }

  /**
   * Update objective progress display
   */
  private updateObjectiveDisplay(): void {
    // Remove old objective texts
    const existingTexts = this.objectivePanel.list.filter(
      child => child instanceof Phaser.GameObjects.Text && child.getData('type') === 'objective'
    );
    existingTexts.forEach(text => text.destroy());

    // Add current objectives
    this.objectives.forEach((progress, index) => {
      const y = -10 + index * 20;
      const text = this.getObjectiveText(progress);
      const color = progress.complete ? '#68d391' : '#e2e8f0';

      const objText = this.add.text(0, y, text, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color,
      }).setOrigin(0.5);
      objText.setData('type', 'objective');

      if (progress.complete) {
        objText.setText('✓ ' + text);
      }

      this.objectivePanel.add(objText);
    });
  }

  /**
   * Get human-readable objective text
   */
  private getObjectiveText(progress: ObjectiveProgress): string {
    const obj = progress.objective;

    switch (obj.type) {
      case 'find_cells':
        return `Find cells: ${progress.current}/${progress.target}`;
      case 'identify_nonliving':
        return `Identify non-living: ${progress.current}/${progress.target}`;
      case 'catalog_cells':
        return `Catalog cells: ${progress.current}/${progress.target}`;
      default:
        return `${obj.type}: ${progress.current}/${progress.target}`;
    }
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Scanner events
    this.events.on('scanner:complete', this.handleScanComplete, this);
    this.events.on('evidence:collected', this.handleEvidenceCollected, this);

    // Keyboard shortcuts
    this.input.keyboard?.on('keydown-J', () => this.openJournal());
    this.input.keyboard?.on('keydown-ESC', () => this.pauseGame());

    // Pan/drag controls
    this.input.on('pointerdown', this.handlePanStart, this);
    this.input.on('pointermove', this.handlePanMove, this);
    this.input.on('pointerup', this.handlePanEnd, this);
    this.input.on('pointerupoutside', this.handlePanEnd, this);
  }

  /**
   * Handle pan start (mouse/touch down)
   */
  private handlePanStart(pointer: Phaser.Input.Pointer): void {
    // Allow left-click drag for panning
    // The scanner handles specimen detection separately
    if (pointer.leftButtonDown()) {
      this.startPan(pointer);
    }
  }

  /**
   * Start panning
   */
  private startPan(pointer: Phaser.Input.Pointer): void {
    this.isDragging = true;
    this.dragStartX = pointer.x;
    this.dragStartY = pointer.y;
    this.slideStartX = this.slideContainer.x;
    this.slideStartY = this.slideContainer.y;
    this.input.setDefaultCursor('grabbing');
  }

  /**
   * Handle pan movement
   */
  private handlePanMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isDragging) return;

    const dx = pointer.x - this.dragStartX;
    const dy = pointer.y - this.dragStartY;

    // Calculate new position with bounds - limit pan to keep specimens in view
    const { width, height } = this.cameras.main;
    const maxPanX = width * 0.35;
    const maxPanY = height * 0.3;

    const newX = Phaser.Math.Clamp(this.slideStartX + dx, width / 2 - maxPanX, width / 2 + maxPanX);
    const newY = Phaser.Math.Clamp(this.slideStartY + dy, height / 2 - maxPanY, height / 2 + maxPanY);

    // Calculate actual movement (may be clamped)
    const actualDx = newX - this.slideContainer.x;
    const actualDy = newY - this.slideContainer.y;

    this.slideContainer.setPosition(newX, newY);

    // Move all specimen containers along with the slide
    this.moveSpecimens(actualDx, actualDy);
  }

  /**
   * Handle pan end
   */
  private handlePanEnd(): void {
    if (this.isDragging) {
      this.isDragging = false;
      this.input.setDefaultCursor('default');
    }
  }

  /**
   * Move all specimens when panning
   */
  private moveSpecimens(dx: number, dy: number): void {
    if (dx === 0 && dy === 0) return;

    // Move all specimen containers and update their positions
    for (const placed of this.placedSpecimens.values()) {
      // Move the visual container
      placed.container.x += dx;
      placed.container.y += dy;

      // Update specimen position for scanner
      placed.specimen.position.x += dx;
      placed.specimen.position.y += dy;

      // Update bounds for scanner detection
      if (placed.specimen.bounds) {
        placed.specimen.bounds.x += dx;
        placed.specimen.bounds.y += dy;
      }
    }
  }

  /**
   * Handle scan completion
   */
  private handleScanComplete(event: ScanCompleteEvent): void {
    const placed = this.placedSpecimens.get(event.specimen.id);
    if (placed) {
      // Track first scan for objective progress (only count once)
      if (!placed.scanned) {
        placed.scanned = true;

        // Update objective progress (delay level completion check to allow scan popup to show first)
        if (event.specimen.isLiving) {
          this.discoveredCells.add(event.specimen.id);
          this.updateObjective('find_cells', this.discoveredCells.size, true);
        } else {
          this.identifiedNonLiving.add(event.specimen.id);
          this.updateObjective('identify_nonliving', this.identifiedNonLiving.size, true);
        }
      }

      // Play discovery sound
      this.events.emit('sound:discovery');

      // Always show scan result popup (every scan, not just first time)
      this.showScanResultPopup(placed, event.result);
    }
  }

  /**
   * Show scan result popup with specimen preview
   */
  private showScanResultPopup(placed: PlacedSpecimen, result: ScanResult): void {
    // Close any existing popup
    if (this.scanPopup) {
      this.scanPopup.destroy();
    }

    const { width, height } = this.cameras.main;
    this.scanPopup = this.add.container(width / 2, height / 2);
    this.scanPopup.setDepth(1500);

    // Semi-transparent background overlay
    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.6);
    overlay.fillRect(-width / 2, -height / 2, width, height);
    overlay.setInteractive(new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height), Phaser.Geom.Rectangle.Contains);

    // Popup panel
    const panelWidth = 400;
    const panelHeight = 520;
    const panel = this.add.graphics();
    panel.fillStyle(0x0a1628, 0.95);
    panel.fillRoundedRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 15);
    panel.lineStyle(2, placed.specimen.isLiving ? 0x68d391 : 0xf6ad55, 0.9);
    panel.strokeRoundedRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 15);

    // Title
    const titleText = placed.specimen.isLiving ? '🔬 CELL DISCOVERED!' : '🔍 NON-LIVING IDENTIFIED';
    const titleColor = placed.specimen.isLiving ? '#68d391' : '#f6ad55';
    const title = this.add.text(0, -panelHeight / 2 + 30, titleText, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '22px',
      color: titleColor,
    }).setOrigin(0.5);

    // Add base elements to popup first (so buttons render on top)
    this.scanPopup.add([overlay, panel, title]);

    // Specimen preview area (circular viewport)
    const previewRadius = 100;
    const previewY = -20;

    // Preview background (dark circle)
    const previewBg = this.add.graphics();
    previewBg.fillStyle(0x1a365d, 1);
    previewBg.fillCircle(0, previewY, previewRadius + 5);
    previewBg.lineStyle(3, 0x4299e1, 0.8);
    previewBg.strokeCircle(0, previewY, previewRadius + 5);
    this.scanPopup.add(previewBg);

    // Clone the specimen for preview (scaled up)
    const previewContainer = this.createSpecimenPreview(placed, previewY, previewRadius);
    if (previewContainer) {
      this.scanPopup.add(previewContainer);
    }

    // Scan results section
    const resultsY = previewY + previewRadius + 40;

    // Divider line
    const divider = this.add.graphics();
    divider.lineStyle(1, 0x4299e1, 0.5);
    divider.lineBetween(-panelWidth / 2 + 30, resultsY - 15, panelWidth / 2 - 30, resultsY - 15);
    this.scanPopup.add(divider);

    // Result details
    const details = [
      { label: 'Classification', value: placed.specimen.isLiving ? 'Living Organism' : 'Non-Living Matter' },
      { label: 'Cell Count', value: this.formatCellCount(result.cellCount) },
      { label: 'Cell Type', value: this.formatCellType(result.cellType) },
    ];

    details.forEach((detail, index) => {
      const y = resultsY + index * 35;

      this.add.text(-panelWidth / 2 + 40, y, detail.label + ':', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#90cdf4',
      }).setOrigin(0, 0.5);

      this.add.text(panelWidth / 2 - 40, y, detail.value, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#e2e8f0',
      }).setOrigin(1, 0.5);

      this.scanPopup!.add(this.children.list.slice(-2));
    });

    // Buttons - show catalog button for living cells (enabled or disabled), otherwise just continue
    const buttonsY = panelHeight / 2 - 35;

    if (placed.specimen.isLiving) {
      // Catalog button for living cells (disabled if already cataloged)
      const isAlreadyCataloged = placed.cataloged;
      const catalogBtn = this.add.container(-70, buttonsY);
      const catalogBtnBg = this.add.graphics();

      // Use gray color if already cataloged, green if not
      const btnColor = isAlreadyCataloged ? 0x2d3748 : 0x38a169;
      catalogBtnBg.fillStyle(btnColor, 1);
      catalogBtnBg.fillRoundedRect(-65, -18, 130, 36, 8);

      const catalogBtnText = this.add.text(0, 0, isAlreadyCataloged ? '✓ CATALOGED' : '📖 CATALOG', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: isAlreadyCataloged ? '#a0aec0' : '#ffffff',
      }).setOrigin(0.5);

      catalogBtn.add([catalogBtnBg, catalogBtnText]);
      catalogBtn.setSize(130, 36);

      // Only make interactive if not already cataloged
      if (!isAlreadyCataloged) {
        catalogBtn.setInteractive({ useHandCursor: true });

        catalogBtn.on('pointerover', () => {
          catalogBtnBg.clear();
          catalogBtnBg.fillStyle(0x48bb78, 1);
          catalogBtnBg.fillRoundedRect(-65, -18, 130, 36, 8);
        });
        catalogBtn.on('pointerout', () => {
          catalogBtnBg.clear();
          catalogBtnBg.fillStyle(0x38a169, 1);
          catalogBtnBg.fillRoundedRect(-65, -18, 130, 36, 8);
        });
        catalogBtn.on('pointerdown', () => {
          this.catalogCell(placed.id);
          // Update button to show cataloged
          catalogBtnBg.clear();
          catalogBtnBg.fillStyle(0x2d3748, 1);
          catalogBtnBg.fillRoundedRect(-65, -18, 130, 36, 8);
          catalogBtnText.setText('✓ CATALOGED');
          catalogBtnText.setColor('#a0aec0');
          catalogBtn.disableInteractive();
        });
      }

      this.scanPopup!.add(catalogBtn);

      // Continue button (secondary, on the right)
      const closeBtn = this.add.container(70, buttonsY);
      const closeBtnBg = this.add.graphics();
      closeBtnBg.fillStyle(0x2b6cb0, 1);
      closeBtnBg.fillRoundedRect(-50, -18, 100, 36, 8);

      const closeBtnText = this.add.text(0, 0, 'CONTINUE', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#ffffff',
      }).setOrigin(0.5);

      closeBtn.add([closeBtnBg, closeBtnText]);
      closeBtn.setSize(100, 36);
      closeBtn.setInteractive({ useHandCursor: true });

      closeBtn.on('pointerover', () => {
        closeBtnBg.clear();
        closeBtnBg.fillStyle(0x3182ce, 1);
        closeBtnBg.fillRoundedRect(-50, -18, 100, 36, 8);
      });
      closeBtn.on('pointerout', () => {
        closeBtnBg.clear();
        closeBtnBg.fillStyle(0x2b6cb0, 1);
        closeBtnBg.fillRoundedRect(-50, -18, 100, 36, 8);
      });
      closeBtn.on('pointerdown', () => this.closeScanPopup());

      this.scanPopup!.add(closeBtn);
    } else {
      // Just continue button for non-living specimens
      const closeBtn = this.add.container(0, buttonsY);
      const closeBtnBg = this.add.graphics();
      closeBtnBg.fillStyle(0x2b6cb0, 1);
      closeBtnBg.fillRoundedRect(-60, -18, 120, 36, 8);

      const closeBtnText = this.add.text(0, 0, 'CONTINUE', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#ffffff',
      }).setOrigin(0.5);

      closeBtn.add([closeBtnBg, closeBtnText]);
      closeBtn.setSize(120, 36);
      closeBtn.setInteractive({ useHandCursor: true });

      closeBtn.on('pointerover', () => {
        closeBtnBg.clear();
        closeBtnBg.fillStyle(0x3182ce, 1);
        closeBtnBg.fillRoundedRect(-60, -18, 120, 36, 8);
      });
      closeBtn.on('pointerout', () => {
        closeBtnBg.clear();
        closeBtnBg.fillStyle(0x2b6cb0, 1);
        closeBtnBg.fillRoundedRect(-60, -18, 120, 36, 8);
      });
      closeBtn.on('pointerdown', () => this.closeScanPopup());

      this.scanPopup!.add(closeBtn);
    }

    // Also close on overlay click
    overlay.on('pointerdown', () => this.closeScanPopup());

    // Animate popup entrance
    this.scanPopup.setScale(0.8);
    this.scanPopup.setAlpha(0);
    this.tweens.add({
      targets: this.scanPopup,
      scale: 1,
      alpha: 1,
      duration: 200,
      ease: 'Back.easeOut',
    });
  }

  /**
   * Create a preview of the specimen in the popup
   * Returns the preview container so it can be added to the popup
   */
  private createSpecimenPreview(placed: PlacedSpecimen, centerY: number, radius: number): Phaser.GameObjects.Container | null {
    const isLiving = isLivingSpecimen(placed.templateId);

    if (isLiving) {
      // Create a new cell for preview using CellRenderer directly
      const template = getOrganismTemplate(placed.templateId);
      if (template) {
        const renderer = new CellRenderer(this);
        const cellResult = renderer.renderCell(template.cellConfig, Date.now());

        // Scale to fit preview area - account for actual rendered size based on shape
        const cellSize = template.cellConfig.size.max;
        const shape = template.cellConfig.shape || 'spherical';

        // Different shapes have different size multipliers in CellRenderer:
        // - Spiral: length = radius * 5, so actual width is ~5x base radius
        // - Rod: width = radius * 2.8, so actual width is ~2.8x base radius
        // - Spherical: diameter = radius * 2, so actual width is ~2x base radius
        let sizeMultiplier = 2; // Default for spherical
        if (shape === 'spiral') {
          sizeMultiplier = 5;
        } else if (shape === 'rod') {
          sizeMultiplier = 2.8;
        }

        // Calculate scale so the cell fits within the preview circle
        // We want the largest dimension to fit within (radius * 1.6) to leave some margin
        const actualSize = cellSize * sizeMultiplier;
        const targetSize = radius * 1.6;
        const scale = targetSize / actualSize;

        cellResult.container.setScale(scale);
        // Position relative to popup center (0, centerY)
        cellResult.container.setPosition(0, centerY);

        return cellResult.container;
      }
    } else {
      // Create a new non-living preview
      const template = getNonLivingTemplate(placed.templateId);
      if (template) {
        const rendered = this.nonLivingGenerator.generate(template, {
          size: radius * 1.8,
          seed: Date.now(),
        });
        // Position relative to popup center (0, centerY)
        rendered.container.setPosition(0, centerY);
        return rendered.container;
      }
    }
    return null;
  }

  /**
   * Format cell count for display
   */
  private formatCellCount(count: ScanResult['cellCount']): string {
    if (count === 'none') return 'No cells';
    if (count === 'single') return 'Single cell';
    if (count === 'multiple') return 'Multiple cells';
    return `${count} cells`;
  }

  /**
   * Format cell type for display
   */
  private formatCellType(type: ScanResult['cellType']): string {
    switch (type) {
      case 'prokaryotic': return 'Prokaryotic';
      case 'eukaryotic': return 'Eukaryotic';
      case 'eukaryotic-plant': return 'Plant Cell';
      case 'none': return 'N/A';
      default: return type;
    }
  }

  /**
   * Close the scan result popup
   */
  private closeScanPopup(): void {
    if (this.scanPopup) {
      this.tweens.add({
        targets: this.scanPopup,
        scale: 0.8,
        alpha: 0,
        duration: 150,
        ease: 'Power2',
        onComplete: () => {
          this.scanPopup?.destroy();
          this.scanPopup = undefined;
        },
      });
    }
  }

  /**
   * Handle evidence collection
   */
  private handleEvidenceCollected(evidence: EvidenceCard): void {
    // Check for duplicates
    if (!this.collectedEvidence.find(e => e.id === evidence.id)) {
      this.collectedEvidence.push(evidence);
      this.evidenceCards.addCard(evidence, true);
    }
  }

  /**
   * Update objective progress
   */
  private updateObjective(type: string, value: number, delayCheck = false): void {
    const objective = this.objectives.find(o => o.objective.type === type);
    if (objective && !objective.complete) {
      objective.current = value;
      if (objective.current >= objective.target) {
        objective.complete = true;
        this.events.emit('objective:complete', objective);
      }
      this.updateObjectiveDisplay();

      // Delay level completion check if requested (e.g., after scan popup)
      if (delayCheck) {
        this.time.delayedCall(1500, () => {
          this.checkLevelComplete();
        });
      } else {
        this.checkLevelComplete();
      }
    }
  }

  /**
   * Update classification objective based on classified cells
   */
  private updateClassifyObjective(): void {
    // Count correct classifications
    let correctCount = 0;
    let totalClassified = 0;

    this.classifiedCells.forEach((classification, specimenId) => {
      const placed = this.placedSpecimens.get(specimenId);
      if (placed) {
        totalClassified++;
        const isActuallyLiving = placed.specimen.isLiving;
        const classifiedAsLiving = classification === 'living';
        if (isActuallyLiving === classifiedAsLiving) {
          correctCount++;
        }
      }
    });

    // Update classify_correctly objective if it exists
    const classifyObjective = this.objectives.find(o => o.objective.type === 'classify_correctly');
    if (classifyObjective && totalClassified > 0) {
      const accuracy = correctCount / totalClassified;
      classifyObjective.current = accuracy;
      if (accuracy >= (classifyObjective.objective.accuracy ?? 0.8)) {
        classifyObjective.complete = true;
        this.events.emit('objective:complete', classifyObjective);
      }
      this.updateObjectiveDisplay();
      this.checkLevelComplete();
    }
  }

  /**
   * Check if all objectives are complete
   */
  private checkLevelComplete(): void {
    const allComplete = this.objectives.every(o => o.complete);
    if (allComplete) {
      this.handleLevelComplete();
    }
  }

  /**
   * Handle level completion
   */
  private handleLevelComplete(): void {
    // Mark level as complete in progress storage
    markLevelComplete(this.levelId);

    this.events.emit('level:complete', {
      levelId: this.levelId,
      discoveries: this.discoveredCells.size,
      evidence: this.collectedEvidence.length,
    });

    // Show completion overlay
    this.showCompletionOverlay();
  }

  /**
   * Show level completion overlay
   */
  private showCompletionOverlay(): void {
    const { width, height } = this.cameras.main;

    // Close any existing scan popup first
    if (this.scanPopup) {
      this.scanPopup.destroy();
      this.scanPopup = undefined;
    }

    this.completionOverlay = this.add.container(width / 2, height / 2);
    this.completionOverlay.setDepth(2000);

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.8);
    bg.fillRect(-width / 2, -height / 2, width, height);

    // Panel - larger to fit more content
    const panelWidth = 450;
    const panelHeight = 420;
    const panel = this.add.graphics();
    panel.fillStyle(0x0a1628, 0.98);
    panel.fillRoundedRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 15);
    panel.lineStyle(3, 0x68d391, 0.9);
    panel.strokeRoundedRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 15);

    // Success glow effect
    for (let i = 5; i >= 0; i--) {
      const glowPanel = this.add.graphics();
      glowPanel.lineStyle(1, 0x68d391, 0.05 * (5 - i));
      glowPanel.strokeRoundedRect(
        -panelWidth / 2 - i * 3,
        -panelHeight / 2 - i * 3,
        panelWidth + i * 6,
        panelHeight + i * 6,
        15 + i
      );
      this.completionOverlay.add(glowPanel);
    }

    // Celebration icon
    const starIcon = this.add.text(0, -160, '★', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '48px',
      color: '#ffd700',
    }).setOrigin(0.5);
    this.tweens.add({
      targets: starIcon,
      scale: 1.2,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Title
    const title = this.add.text(0, -105, 'LEVEL COMPLETE!', {
      fontFamily: '"Courier New", monospace',
      fontSize: '32px',
      fontStyle: 'bold',
      color: '#68d391',
    }).setOrigin(0.5);

    // Level name
    const levelName = this.add.text(0, -70, this.level?.name || 'Level', {
      fontFamily: 'Georgia, serif',
      fontSize: '18px',
      fontStyle: 'italic',
      color: '#8ba4b4',
    }).setOrigin(0.5);

    // Divider
    const divider = this.add.graphics();
    divider.lineStyle(1, 0x68d391, 0.3);
    divider.lineBetween(-150, -45, 150, -45);

    // Stats section with icons
    const stats = [
      { icon: '🔬', label: 'Cells Discovered', value: this.discoveredCells.size, color: '#68d391' },
      { icon: '📋', label: 'Evidence Collected', value: this.collectedEvidence.length, color: '#00d4ff' },
      { icon: '🔍', label: 'Non-living Identified', value: this.identifiedNonLiving.size, color: '#f6ad55' },
      { icon: '📖', label: 'Cells Cataloged', value: this.catalogedCells.size, color: '#b794f4' },
    ];

    stats.forEach((stat, i) => {
      const y = -20 + i * 35;

      // Icon
      const icon = this.add.text(-160, y, stat.icon, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
      }).setOrigin(0.5);

      // Label
      const label = this.add.text(-130, y, stat.label, {
        fontFamily: 'Georgia, serif',
        fontSize: '15px',
        color: '#8ba4b4',
      }).setOrigin(0, 0.5);

      // Value
      const value = this.add.text(160, y, stat.value.toString(), {
        fontFamily: '"Courier New", monospace',
        fontSize: '20px',
        fontStyle: 'bold',
        color: stat.color,
      }).setOrigin(1, 0.5);

      this.completionOverlay!.add([icon, label, value]);
    });

    // Objectives summary
    const completedObjectives = this.objectives.filter(o => o.complete).length;
    const totalObjectives = this.objectives.length;

    const objectiveSummary = this.add.text(0, 135, `Objectives: ${completedObjectives}/${totalObjectives} ✓`, {
      fontFamily: '"Courier New", monospace',
      fontSize: '14px',
      color: '#68d391',
    }).setOrigin(0.5);

    // Buttons container
    const buttonsY = 180;

    // Next Level button (primary)
    const nextBtnContainer = this.add.container(0, buttonsY);
    const nextBtnBg = this.add.graphics();
    nextBtnBg.fillStyle(0x2b6cb0, 1);
    nextBtnBg.fillRoundedRect(-90, -22, 180, 44, 10);

    const nextBtnText = this.add.text(0, 0, 'NEXT LEVEL →', {
      fontFamily: '"Courier New", monospace',
      fontSize: '16px',
      fontStyle: 'bold',
      color: '#ffffff',
    }).setOrigin(0.5);

    nextBtnContainer.add([nextBtnBg, nextBtnText]);
    nextBtnContainer.setSize(180, 44);
    nextBtnContainer.setInteractive({ useHandCursor: true });

    nextBtnContainer.on('pointerdown', () => this.goToNextLevel());
    nextBtnContainer.on('pointerover', () => {
      nextBtnBg.clear();
      nextBtnBg.fillStyle(0x3182ce, 1);
      nextBtnBg.fillRoundedRect(-90, -22, 180, 44, 10);
    });
    nextBtnContainer.on('pointerout', () => {
      nextBtnBg.clear();
      nextBtnBg.fillStyle(0x2b6cb0, 1);
      nextBtnBg.fillRoundedRect(-90, -22, 180, 44, 10);
    });

    // Menu button (secondary)
    const menuBtnContainer = this.add.container(-70, buttonsY + 55);
    const menuBtnBg = this.add.graphics();
    menuBtnBg.fillStyle(0x1a365d, 1);
    menuBtnBg.fillRoundedRect(-55, -15, 110, 30, 6);
    menuBtnBg.lineStyle(1, 0x4a5568, 0.5);
    menuBtnBg.strokeRoundedRect(-55, -15, 110, 30, 6);

    const menuBtnText = this.add.text(0, 0, '← MENU', {
      fontFamily: '"Courier New", monospace',
      fontSize: '12px',
      color: '#a0aec0',
    }).setOrigin(0.5);

    menuBtnContainer.add([menuBtnBg, menuBtnText]);
    menuBtnContainer.setSize(110, 30);
    menuBtnContainer.setInteractive({ useHandCursor: true });

    menuBtnContainer.on('pointerdown', () => this.exitLevel());
    menuBtnContainer.on('pointerover', () => {
      menuBtnBg.clear();
      menuBtnBg.fillStyle(0x2d3748, 1);
      menuBtnBg.fillRoundedRect(-55, -15, 110, 30, 6);
      menuBtnText.setColor('#e2e8f0');
    });
    menuBtnContainer.on('pointerout', () => {
      menuBtnBg.clear();
      menuBtnBg.fillStyle(0x1a365d, 1);
      menuBtnBg.fillRoundedRect(-55, -15, 110, 30, 6);
      menuBtnBg.lineStyle(1, 0x4a5568, 0.5);
      menuBtnBg.strokeRoundedRect(-55, -15, 110, 30, 6);
      menuBtnText.setColor('#a0aec0');
    });

    // Replay button (secondary)
    const replayBtnContainer = this.add.container(70, buttonsY + 55);
    const replayBtnBg = this.add.graphics();
    replayBtnBg.fillStyle(0x1a365d, 1);
    replayBtnBg.fillRoundedRect(-55, -15, 110, 30, 6);
    replayBtnBg.lineStyle(1, 0x4a5568, 0.5);
    replayBtnBg.strokeRoundedRect(-55, -15, 110, 30, 6);

    const replayBtnText = this.add.text(0, 0, '↺ REPLAY', {
      fontFamily: '"Courier New", monospace',
      fontSize: '12px',
      color: '#a0aec0',
    }).setOrigin(0.5);

    replayBtnContainer.add([replayBtnBg, replayBtnText]);
    replayBtnContainer.setSize(110, 30);
    replayBtnContainer.setInteractive({ useHandCursor: true });

    replayBtnContainer.on('pointerdown', () => this.replayLevel());
    replayBtnContainer.on('pointerover', () => {
      replayBtnBg.clear();
      replayBtnBg.fillStyle(0x2d3748, 1);
      replayBtnBg.fillRoundedRect(-55, -15, 110, 30, 6);
      replayBtnText.setColor('#e2e8f0');
    });
    replayBtnContainer.on('pointerout', () => {
      replayBtnBg.clear();
      replayBtnBg.fillStyle(0x1a365d, 1);
      replayBtnBg.fillRoundedRect(-55, -15, 110, 30, 6);
      replayBtnBg.lineStyle(1, 0x4a5568, 0.5);
      replayBtnBg.strokeRoundedRect(-55, -15, 110, 30, 6);
      replayBtnText.setColor('#a0aec0');
    });

    this.completionOverlay.add([
      bg, panel, starIcon, title, levelName, divider, objectiveSummary,
      nextBtnContainer, menuBtnContainer, replayBtnContainer
    ]);

    // Entrance animation
    this.completionOverlay.setAlpha(0);
    this.completionOverlay.setScale(0.8);
    this.tweens.add({
      targets: this.completionOverlay,
      alpha: 1,
      scale: 1,
      duration: 300,
      ease: 'Back.easeOut',
    });
  }

  /**
   * Go to next level
   */
  private goToNextLevel(): void {
    // Parse current level ID to get next level
    const match = this.levelId.match(/^(\d+)-(\d+)$/);
    // Check if this is a boss level (format: boss-{chapterNum})
    const bossMatch = this.levelId.match(/^boss-(\d+)$/);
    if (bossMatch) {
      // After boss, go to first level of next chapter
      const chapterNum = parseInt(bossMatch[1]);
      const nextChapterNum = chapterNum + 1;
      const nextChapter = CHAPTERS.find((c) => c.id.includes(`ch${nextChapterNum}`));
      if (nextChapter && nextChapter.levels.length > 0) {
        const firstLevelOfNextChapter = nextChapter.levels[0].id;
        this.cleanup();
        this.scene.start('DiscoveryScene', { levelId: firstLevelOfNextChapter });
        return;
      }
      // No next chapter, go to menu
      this.exitLevel();
      return;
    }

    if (match) {
      const chapterNum = parseInt(match[1]);
      const levelNum = parseInt(match[2]);
      const nextLevelId = `${chapterNum}-${levelNum + 1}`;

      // Check if next level exists in current chapter
      const currentChapter = CHAPTERS.find((c) => c.id.includes(`ch${chapterNum}`));
      if (currentChapter) {
        const nextLevel = currentChapter.levels.find((l) => l.id === nextLevelId);
        if (nextLevel) {
          this.cleanup();
          this.scene.start('DiscoveryScene', { levelId: nextLevelId });
          return;
        }

        // No more regular levels - go to boss challenge if it exists
        if (currentChapter.bossChallenge) {
          const bossLevelId = `boss-${chapterNum}`;
          this.cleanup();
          this.scene.start('DiscoveryScene', { levelId: bossLevelId });
          return;
        }

        // No boss challenge - go to next chapter
        const nextChapterNum = chapterNum + 1;
        const nextChapter = CHAPTERS.find((c) => c.id.includes(`ch${nextChapterNum}`));
        if (nextChapter && nextChapter.levels.length > 0) {
          const firstLevelOfNextChapter = nextChapter.levels[0].id;
          this.cleanup();
          this.scene.start('DiscoveryScene', { levelId: firstLevelOfNextChapter });
          return;
        }
      }
    }

    // If no next level or chapter, go to menu
    this.exitLevel();
  }

  /**
   * Replay current level
   */
  private replayLevel(): void {
    this.cleanup();
    this.scene.start('DiscoveryScene', { levelId: this.levelId });
  }

  /**
   * Open journal (cell catalog)
   */
  private openJournal(): void {
    if (this.journalOverlay) return; // Already open

    const { width, height } = this.cameras.main;
    this.journalOverlay = this.add.container(width / 2, height / 2);
    this.journalOverlay.setDepth(2000);

    // Bioluminescent background overlay with gradient
    const overlay = this.add.graphics();
    overlay.fillStyle(0x020810, 0.95);
    overlay.fillRect(-width / 2, -height / 2, width, height);
    overlay.setInteractive(new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height), Phaser.Geom.Rectangle.Contains);

    // Main journal panel with glow effect
    const panelWidth = Math.min(700, width - 80);
    const panelHeight = Math.min(550, height - 80);

    // Outer glow
    const glow = this.add.graphics();
    glow.fillStyle(0x00ffaa, 0.08);
    glow.fillRoundedRect(-panelWidth / 2 - 10, -panelHeight / 2 - 10, panelWidth + 20, panelHeight + 20, 20);
    glow.fillStyle(0x00ffaa, 0.05);
    glow.fillRoundedRect(-panelWidth / 2 - 20, -panelHeight / 2 - 20, panelWidth + 40, panelHeight + 40, 25);

    // Panel background
    const panel = this.add.graphics();
    panel.fillStyle(0x051520, 0.98);
    panel.fillRoundedRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 15);
    panel.lineStyle(2, 0x00ffaa, 0.6);
    panel.strokeRoundedRect(-panelWidth / 2, -panelHeight / 2, panelWidth, panelHeight, 15);

    // DNA helix decorative element (left side)
    const dnaHelix = this.add.graphics();
    dnaHelix.lineStyle(1.5, 0x00ffaa, 0.3);
    for (let i = 0; i < 12; i++) {
      const y = -panelHeight / 2 + 40 + i * 40;
      const x1 = -panelWidth / 2 + 20 + Math.sin(i * 0.8) * 8;
      const x2 = -panelWidth / 2 + 35 + Math.sin(i * 0.8 + Math.PI) * 8;
      dnaHelix.lineBetween(x1, y, x2, y);
      dnaHelix.fillStyle(0x00ffaa, 0.4);
      dnaHelix.fillCircle(x1, y, 2);
      dnaHelix.fillCircle(x2, y, 2);
    }

    // Header with gradient accent
    const headerBg = this.add.graphics();
    headerBg.fillGradientStyle(0x00ffaa, 0x00ffaa, 0x006655, 0x006655, 0.15, 0.15, 0.05, 0.05);
    headerBg.fillRect(-panelWidth / 2, -panelHeight / 2, panelWidth, 60);

    // Title
    const title = this.add.text(0, -panelHeight / 2 + 30, '📖  CELL JOURNAL', {
      fontFamily: '"Courier New", monospace',
      fontSize: '26px',
      color: '#00ffaa',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // Subtitle with stats
    const catalogCount = this.catalogedCells.size;
    const subtitle = this.add.text(0, -panelHeight / 2 + 58, `${catalogCount} specimen${catalogCount !== 1 ? 's' : ''} cataloged`, {
      fontFamily: '"Courier New", monospace',
      fontSize: '12px',
      color: '#4ecdc4',
    }).setOrigin(0.5);

    // Divider line
    const divider = this.add.graphics();
    divider.lineStyle(1, 0x00ffaa, 0.3);
    divider.lineBetween(-panelWidth / 2 + 50, -panelHeight / 2 + 75, panelWidth / 2 - 50, -panelHeight / 2 + 75);

    // Content area
    const contentY = -panelHeight / 2 + 95;
    const contentHeight = panelHeight - 160;

    // Content container
    const contentContainer = this.add.container(0, contentY);

    if (catalogCount === 0) {
      // Empty state with bioluminescent aesthetic
      const emptyGlow = this.add.graphics();
      emptyGlow.fillStyle(0x00ffaa, 0.03);
      emptyGlow.fillCircle(0, contentHeight / 2 - 20, 60);

      const emptyIcon = this.add.text(0, contentHeight / 2 - 40, '🔬', {
        fontSize: '48px',
      }).setOrigin(0.5).setAlpha(0.5);

      const emptyText = this.add.text(0, contentHeight / 2 + 20, 'No cells cataloged yet', {
        fontFamily: '"Courier New", monospace',
        fontSize: '16px',
        color: '#4a6670',
      }).setOrigin(0.5);

      const emptyHint = this.add.text(0, contentHeight / 2 + 50, 'Scan cells and click CATALOG to add them here', {
        fontFamily: '"Courier New", monospace',
        fontSize: '12px',
        color: '#3a5660',
      }).setOrigin(0.5);

      contentContainer.add([emptyGlow, emptyIcon, emptyText, emptyHint]);
    } else {
      // ═══════════════════════════════════════════════════════════════════════
      // REDESIGNED SPECIMEN CARDS - Scientific Lab Notebook Aesthetic
      // ═══════════════════════════════════════════════════════════════════════
      let row = 0;
      const cardWidth = 320;
      const cardHeight = 230;
      const cardsPerRow = Math.floor((panelWidth - 40) / (cardWidth + 15));
      const startX = -((cardsPerRow - 1) * (cardWidth + 15)) / 2;

      this.catalogedCells.forEach((specimenId) => {
        const placed = this.placedSpecimens.get(specimenId);
        if (!placed) return;

        const col = row % cardsPerRow;
        const rowNum = Math.floor(row / cardsPerRow);
        const x = startX + col * (cardWidth + 15);
        const y = rowNum * (cardHeight + 15) + cardHeight / 2;

        // Card container
        const card = this.add.container(x, y);

        // Get current classification status
        const currentClassification = this.classifiedCells.get(specimenId);
        const isClassified = !!currentClassification;
        const isCorrect = isClassified && (
          (currentClassification === 'living' && placed.specimen.isLiving) ||
          (currentClassification === 'non-living' && !placed.specimen.isLiving)
        );

        // Get template info
        const template = getOrganismTemplate(placed.templateId);
        const name = template?.name || 'Unknown';
        const isLiving = placed.specimen.isLiving;
        const cellType = placed.specimen.cellType;

        // ─────────────────────────────────────────────────────────────
        // CARD BACKGROUND - Layered glass morphism effect
        // ─────────────────────────────────────────────────────────────
        const cardBg = this.add.graphics();

        // Outer glow based on classification status
        if (isClassified) {
          const glowColor = isCorrect ? 0x00ff88 : 0xff6b6b;
          cardBg.fillStyle(glowColor, 0.12);
          cardBg.fillRoundedRect(-cardWidth / 2 - 6, -cardHeight / 2 - 6, cardWidth + 12, cardHeight + 12, 16);
          cardBg.fillStyle(glowColor, 0.06);
          cardBg.fillRoundedRect(-cardWidth / 2 - 10, -cardHeight / 2 - 10, cardWidth + 20, cardHeight + 20, 18);
        }

        // Main card - dark glass effect
        cardBg.fillStyle(0x040d18, 0.97);
        cardBg.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 12);

        // Inner highlight (top edge)
        cardBg.fillStyle(0x00ffaa, 0.04);
        cardBg.fillRoundedRect(-cardWidth / 2 + 1, -cardHeight / 2 + 1, cardWidth - 2, 40, { tl: 12, tr: 12, bl: 0, br: 0 });

        // Border
        const borderColor = isClassified ? (isCorrect ? 0x00ff88 : 0xff6b6b) : 0x00ffaa;
        cardBg.lineStyle(isClassified ? 2 : 1.5, borderColor, isClassified ? 0.7 : 0.35);
        cardBg.strokeRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 12);

        // ─────────────────────────────────────────────────────────────
        // HEADER SECTION - Two-row layout for clarity
        // ─────────────────────────────────────────────────────────────
        const headerY = -cardHeight / 2 + 16;

        // Top row: Specimen number + Cell type badge
        const specNumBg = this.add.graphics();
        specNumBg.fillStyle(0x00ffaa, 0.15);
        specNumBg.fillRoundedRect(-cardWidth / 2 + 10, headerY - 8, 42, 18, 4);
        specNumBg.lineStyle(1, 0x00ffaa, 0.3);
        specNumBg.strokeRoundedRect(-cardWidth / 2 + 10, headerY - 8, 42, 18, 4);

        const specNum = this.add.text(-cardWidth / 2 + 31, headerY + 1, `#${String(row + 1).padStart(3, '0')}`, {
          fontFamily: '"Courier New", monospace',
          fontSize: '10px',
          color: '#00ffaa',
          fontStyle: 'bold',
        }).setOrigin(0.5);

        // Cell type badge (top right)
        const typeBadge = this.add.graphics();
        const typeStr = cellType.toUpperCase();
        const typeColor = cellType === 'eukaryotic' ? 0x9b59b6 : 0x3498db;
        const typeHexColor = cellType === 'eukaryotic' ? '#c39bd3' : '#85c1e9';
        typeBadge.fillStyle(typeColor, 0.2);
        typeBadge.fillRoundedRect(cardWidth / 2 - 82, headerY - 8, 72, 18, 4);
        typeBadge.lineStyle(1, typeColor, 0.4);
        typeBadge.strokeRoundedRect(cardWidth / 2 - 82, headerY - 8, 72, 18, 4);

        const typeText = this.add.text(cardWidth / 2 - 46, headerY + 1, typeStr, {
          fontFamily: '"Courier New", monospace',
          fontSize: '8px',
          color: typeHexColor,
          fontStyle: 'bold',
        }).setOrigin(0.5);

        // Bottom row: Specimen name (full width, truncated if needed)
        const nameY = headerY + 20;
        const maxNameWidth = cardWidth - 30;

        // Truncate name if too long
        let displayName = name.toUpperCase();
        const nameText = this.add.text(-cardWidth / 2 + 15, nameY, displayName, {
          fontFamily: 'Georgia, serif',
          fontSize: '14px',
          color: '#e8f4f0',
          fontStyle: 'bold',
        }).setOrigin(0, 0.5);

        // Truncate with ellipsis if too wide
        if (nameText.width > maxNameWidth) {
          while (nameText.width > maxNameWidth - 20 && displayName.length > 3) {
            displayName = displayName.slice(0, -1);
            nameText.setText(displayName + '...');
          }
        }

        // ─────────────────────────────────────────────────────────────
        // MAIN CONTENT - Cell preview and data
        // ─────────────────────────────────────────────────────────────
        const contentY = -cardHeight / 2 + 50;

        // Cell preview container (petri dish style)
        const previewX = -cardWidth / 2 + 55;
        const previewY = contentY + 40;
        const previewRadius = 38;

        // Petri dish outer ring
        const dishBg = this.add.graphics();
        dishBg.fillStyle(0x010810, 1);
        dishBg.fillCircle(previewX, previewY, previewRadius + 4);
        dishBg.lineStyle(2, 0x1a3a4a, 0.8);
        dishBg.strokeCircle(previewX, previewY, previewRadius + 4);

        // Dish inner area with gradient effect
        dishBg.fillStyle(0x021520, 1);
        dishBg.fillCircle(previewX, previewY, previewRadius);

        // Dish rim highlight
        dishBg.lineStyle(1, 0x00ffaa, 0.15);
        dishBg.strokeCircle(previewX, previewY, previewRadius - 2);

        // Microscope grid lines
        dishBg.lineStyle(0.5, 0x00ffaa, 0.08);
        dishBg.lineBetween(previewX - previewRadius + 5, previewY, previewX + previewRadius - 5, previewY);
        dishBg.lineBetween(previewX, previewY - previewRadius + 5, previewX, previewY + previewRadius - 5);

        // Cell visualization - more detailed based on type
        const cellViz = this.add.graphics();
        const cellColor = isLiving ? 0x90cdf4 : 0x8899aa;
        const cellRadius = isLiving ? 22 : 18;

        if (isLiving) {
          // Living cell - organic shape with membrane
          cellViz.fillStyle(cellColor, 0.3);
          cellViz.fillCircle(previewX, previewY, cellRadius + 4);

          // Cell membrane
          cellViz.lineStyle(2, cellColor, 0.7);
          cellViz.strokeCircle(previewX, previewY, cellRadius);

          // Cytoplasm
          cellViz.fillStyle(0x4a90a4, 0.5);
          cellViz.fillCircle(previewX, previewY, cellRadius - 2);

          // Nucleus
          cellViz.fillStyle(0x805ad5, 0.8);
          cellViz.fillCircle(previewX + 3, previewY - 2, 8);
          cellViz.fillStyle(0x553c9a, 0.6);
          cellViz.fillCircle(previewX + 4, previewY - 3, 4);

          // Organelles (mitochondria-like)
          cellViz.fillStyle(0xed8936, 0.7);
          cellViz.fillEllipse(previewX - 10, previewY + 8, 6, 3);
          cellViz.fillEllipse(previewX + 12, previewY + 5, 5, 3);
          cellViz.fillEllipse(previewX - 5, previewY - 12, 5, 3);

          // Ribosomes
          cellViz.fillStyle(0xff6b9d, 0.6);
          for (let i = 0; i < 6; i++) {
            const rx = previewX + Math.cos(i * 1.2) * 12;
            const ry = previewY + Math.sin(i * 1.2) * 10;
            cellViz.fillCircle(rx, ry, 1.5);
          }
        } else {
          // Non-living - crystal/geometric shape
          cellViz.fillStyle(cellColor, 0.4);
          cellViz.fillTriangle(
            previewX, previewY - cellRadius,
            previewX - cellRadius * 0.8, previewY + cellRadius * 0.6,
            previewX + cellRadius * 0.8, previewY + cellRadius * 0.6
          );
          cellViz.lineStyle(1.5, 0xaabbcc, 0.6);
          cellViz.strokeTriangle(
            previewX, previewY - cellRadius,
            previewX - cellRadius * 0.8, previewY + cellRadius * 0.6,
            previewX + cellRadius * 0.8, previewY + cellRadius * 0.6
          );
          // Crystal facet highlight
          cellViz.fillStyle(0xffffff, 0.15);
          cellViz.fillTriangle(
            previewX - 2, previewY - cellRadius + 6,
            previewX - cellRadius * 0.5, previewY + 2,
            previewX, previewY
          );
        }

        // ─────────────────────────────────────────────────────────────
        // DATA PANEL - Scientific measurements
        // ─────────────────────────────────────────────────────────────
        const dataX = -cardWidth / 2 + 115;
        const dataY = contentY + 8;

        // Data labels with values
        const dataItems = [
          { label: 'CELL COUNT', value: placed.specimen.cellCount === 1 ? 'Single' : `${placed.specimen.cellCount}`, icon: '⬡' },
          { label: 'STRUCTURE', value: cellType === 'eukaryotic' ? 'Nucleus ✓' : 'No nucleus', icon: '◉' },
          { label: 'STATUS', value: isLiving ? 'Active' : 'Inert', icon: isLiving ? '◈' : '◇' },
        ];

        dataItems.forEach((item, i) => {
          const itemY = dataY + i * 24;

          // Icon
          const icon = this.add.text(dataX, itemY, item.icon, {
            fontFamily: 'system-ui',
            fontSize: '10px',
            color: '#00ffaa',
          }).setOrigin(0, 0.5).setAlpha(0.6);

          // Label
          const label = this.add.text(dataX + 18, itemY, item.label, {
            fontFamily: '"Courier New", monospace',
            fontSize: '8px',
            color: '#4a7080',
          }).setOrigin(0, 0.5);

          // Value
          const value = this.add.text(dataX + 18, itemY + 12, item.value, {
            fontFamily: 'Georgia, serif',
            fontSize: '12px',
            color: '#a8d4e6',
            fontStyle: 'italic',
          }).setOrigin(0, 0.5);

          card.add([icon, label, value]);
        });

        // ─────────────────────────────────────────────────────────────
        // EVIDENCE INDICATORS - Collected proof
        // ─────────────────────────────────────────────────────────────
        const evidenceY = contentY + 82;
        const evidenceLabel = this.add.text(-cardWidth / 2 + 115, evidenceY, 'EVIDENCE:', {
          fontFamily: '"Courier New", monospace',
          fontSize: '8px',
          color: '#4a7080',
        }).setOrigin(0, 0.5);

        // Evidence pills
        const evidenceTypes = ['STRUCT', 'BEHAV', 'COMP'];
        evidenceTypes.forEach((type, i) => {
          const pillX = -cardWidth / 2 + 175 + i * 42;
          const pillBg = this.add.graphics();
          pillBg.fillStyle(0x00ffaa, 0.12);
          pillBg.fillRoundedRect(pillX - 17, evidenceY - 8, 34, 16, 8);

          const pillText = this.add.text(pillX, evidenceY, type, {
            fontFamily: '"Courier New", monospace',
            fontSize: '7px',
            color: '#00ffaa',
          }).setOrigin(0.5);

          card.add([pillBg, pillText]);
        });

        // ─────────────────────────────────────────────────────────────
        // CLASSIFICATION SECTION - Bold action buttons
        // ─────────────────────────────────────────────────────────────
        const classifyY = cardHeight / 2 - 45;

        // Separator line with label
        const separator = this.add.graphics();
        separator.lineStyle(1, 0x00ffaa, 0.2);
        separator.lineBetween(-cardWidth / 2 + 15, classifyY - 22, cardWidth / 2 - 15, classifyY - 22);

        // Classification status badge (centered above buttons)
        const statusText = isClassified
          ? (isCorrect ? '✓ CORRECT' : '✗ INCORRECT')
          : '? UNCLASSIFIED';
        const statusColor = isClassified ? (isCorrect ? 0x00ff88 : 0xff6b6b) : 0x5a8a98;

        const statusBadge = this.add.graphics();
        statusBadge.fillStyle(statusColor, 0.15);
        statusBadge.fillRoundedRect(-45, classifyY - 18, 90, 16, 8);

        const classifyStatus = this.add.text(0, classifyY - 10, statusText, {
          fontFamily: '"Courier New", monospace',
          fontSize: '9px',
          color: isClassified ? (isCorrect ? '#00ff88' : '#ff6b6b') : '#7a9aa8',
          fontStyle: 'bold',
        }).setOrigin(0.5);

        // Large prominent classification buttons
        const btnWidth = 130;
        const btnHeight = 38;
        const btnSpacing = 12;
        const btnY = classifyY + 18;

        // ═══════════════════════════════════════════════════════════
        // LIVING BUTTON - Vibrant green with cell icon
        // ═══════════════════════════════════════════════════════════
        const livingBtn = this.add.container(-btnWidth / 2 - btnSpacing / 2, btnY);
        const livingBg = this.add.graphics();
        const isLivingSelected = currentClassification === 'living';

        // Button background with glow effect
        if (isLivingSelected) {
          // Selected state - bright and prominent
          livingBg.fillStyle(0x00ff88, 0.08);
          livingBg.fillRoundedRect(-btnWidth / 2 - 4, -btnHeight / 2 - 4, btnWidth + 8, btnHeight + 8, 12);
          livingBg.fillStyle(0x00ff88, 0.3);
          livingBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
          livingBg.lineStyle(2.5, 0x00ff88, 1);
          livingBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
        } else {
          // Default state
          livingBg.fillStyle(0x0a2818, 0.9);
          livingBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
          livingBg.lineStyle(2, 0x00ff88, 0.5);
          livingBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
        }

        // Living cell icon - animated look
        const livingIcon = this.add.graphics();
        const iconX = -btnWidth / 2 + 22;
        livingIcon.fillStyle(0x00ff88, isLivingSelected ? 1 : 0.7);
        livingIcon.fillCircle(iconX, 0, 10);
        livingIcon.fillStyle(0x00aa66, 0.8);
        livingIcon.fillCircle(iconX + 2, -1, 4);
        livingIcon.fillStyle(0xffffff, 0.4);
        livingIcon.fillCircle(iconX - 3, -3, 2);
        // Membrane ripple
        livingIcon.lineStyle(1.5, 0x00ff88, isLivingSelected ? 0.6 : 0.3);
        livingIcon.strokeCircle(iconX, 0, 13);

        const livingText = this.add.text(8, 0, '🧬 LIVING', {
          fontFamily: 'Georgia, serif',
          fontSize: '14px',
          color: isLivingSelected ? '#ffffff' : '#00ff88',
          fontStyle: 'bold',
        }).setOrigin(0.5);

        livingBtn.add([livingBg, livingIcon, livingText]);
        livingBtn.setSize(btnWidth, btnHeight);
        livingBtn.setInteractive({ useHandCursor: true });

        // ═══════════════════════════════════════════════════════════
        // NON-LIVING BUTTON - Cool blue with crystal icon
        // ═══════════════════════════════════════════════════════════
        const nonLivingBtn = this.add.container(btnWidth / 2 + btnSpacing / 2, btnY);
        const nonLivingBg = this.add.graphics();
        const isNonLivingSelected = currentClassification === 'non-living';

        // Button background with glow effect
        if (isNonLivingSelected) {
          // Selected state
          nonLivingBg.fillStyle(0x6699cc, 0.08);
          nonLivingBg.fillRoundedRect(-btnWidth / 2 - 4, -btnHeight / 2 - 4, btnWidth + 8, btnHeight + 8, 12);
          nonLivingBg.fillStyle(0x6699cc, 0.3);
          nonLivingBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
          nonLivingBg.lineStyle(2.5, 0x6699cc, 1);
          nonLivingBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
        } else {
          // Default state
          nonLivingBg.fillStyle(0x0a1828, 0.9);
          nonLivingBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
          nonLivingBg.lineStyle(2, 0x6699cc, 0.5);
          nonLivingBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
        }

        // Crystal/geometric icon
        const nonLivingIcon = this.add.graphics();
        const nlIconX = -btnWidth / 2 + 22;
        nonLivingIcon.fillStyle(0x6699cc, isNonLivingSelected ? 1 : 0.7);
        nonLivingIcon.fillTriangle(nlIconX, -10, nlIconX - 9, 6, nlIconX + 9, 6);
        nonLivingIcon.fillStyle(0x99ccff, 0.4);
        nonLivingIcon.fillTriangle(nlIconX - 1, -6, nlIconX - 6, 3, nlIconX + 2, 1);
        nonLivingIcon.lineStyle(1.5, 0x6699cc, isNonLivingSelected ? 0.8 : 0.4);
        nonLivingIcon.strokeTriangle(nlIconX, -10, nlIconX - 9, 6, nlIconX + 9, 6);

        const nonLivingText = this.add.text(8, 0, '💎 NON-LIVING', {
          fontFamily: 'Georgia, serif',
          fontSize: '13px',
          color: isNonLivingSelected ? '#ffffff' : '#6699cc',
          fontStyle: 'bold',
        }).setOrigin(0.5);

        nonLivingBtn.add([nonLivingBg, nonLivingIcon, nonLivingText]);
        nonLivingBtn.setSize(btnWidth, btnHeight);
        nonLivingBtn.setInteractive({ useHandCursor: true });

        // ═══════════════════════════════════════════════════════════
        // HOVER EFFECTS - Responsive feedback
        // ═══════════════════════════════════════════════════════════
        livingBtn.on('pointerover', () => {
          if (!isLivingSelected) {
            livingBg.clear();
            livingBg.fillStyle(0x00ff88, 0.05);
            livingBg.fillRoundedRect(-btnWidth / 2 - 2, -btnHeight / 2 - 2, btnWidth + 4, btnHeight + 4, 10);
            livingBg.fillStyle(0x0a3820, 1);
            livingBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
            livingBg.lineStyle(2.5, 0x00ff88, 0.8);
            livingBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
            livingText.setColor('#00ff88');
            livingText.setScale(1.02);
          }
        });

        livingBtn.on('pointerout', () => {
          if (!isLivingSelected) {
            livingBg.clear();
            livingBg.fillStyle(0x0a2818, 0.9);
            livingBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
            livingBg.lineStyle(2, 0x00ff88, 0.5);
            livingBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
            livingText.setColor('#00ff88');
            livingText.setScale(1);
          }
        });

        nonLivingBtn.on('pointerover', () => {
          if (!isNonLivingSelected) {
            nonLivingBg.clear();
            nonLivingBg.fillStyle(0x6699cc, 0.05);
            nonLivingBg.fillRoundedRect(-btnWidth / 2 - 2, -btnHeight / 2 - 2, btnWidth + 4, btnHeight + 4, 10);
            nonLivingBg.fillStyle(0x102838, 1);
            nonLivingBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
            nonLivingBg.lineStyle(2.5, 0x6699cc, 0.8);
            nonLivingBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
            nonLivingText.setColor('#6699cc');
            nonLivingText.setScale(1.02);
          }
        });

        nonLivingBtn.on('pointerout', () => {
          if (!isNonLivingSelected) {
            nonLivingBg.clear();
            nonLivingBg.fillStyle(0x0a1828, 0.9);
            nonLivingBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
            nonLivingBg.lineStyle(2, 0x6699cc, 0.5);
            nonLivingBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
            nonLivingText.setColor('#6699cc');
            nonLivingText.setScale(1);
          }
        });

        // Click handlers for classification - update in place without closing Journal
        livingBtn.on('pointerdown', () => {
          this.classifiedCells.set(specimenId, 'living');
          this.updateClassifyObjective();

          // Check if correct
          const nowCorrect = placed.specimen.isLiving;
          const newStatusColor = nowCorrect ? 0x00ff88 : 0xff6b6b;
          const newStatusText = nowCorrect ? '✓ CORRECT' : '✗ INCORRECT';

          // Update status badge
          statusBadge.clear();
          statusBadge.fillStyle(newStatusColor, 0.15);
          statusBadge.fillRoundedRect(-45, classifyY - 18, 90, 16, 8);
          classifyStatus.setText(newStatusText);
          classifyStatus.setColor(nowCorrect ? '#00ff88' : '#ff6b6b');

          // Update card border glow
          cardBg.clear();
          cardBg.fillStyle(newStatusColor, 0.12);
          cardBg.fillRoundedRect(-cardWidth / 2 - 6, -cardHeight / 2 - 6, cardWidth + 12, cardHeight + 12, 16);
          cardBg.fillStyle(newStatusColor, 0.06);
          cardBg.fillRoundedRect(-cardWidth / 2 - 10, -cardHeight / 2 - 10, cardWidth + 20, cardHeight + 20, 18);
          cardBg.fillStyle(0x040d18, 0.97);
          cardBg.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 12);
          cardBg.fillStyle(0x00ffaa, 0.04);
          cardBg.fillRoundedRect(-cardWidth / 2 + 1, -cardHeight / 2 + 1, cardWidth - 2, 40, { tl: 12, tr: 12, bl: 0, br: 0 });
          cardBg.lineStyle(2, newStatusColor, 0.7);
          cardBg.strokeRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 12);

          // Update LIVING button to selected state
          livingBg.clear();
          livingBg.fillStyle(0x00ff88, 0.08);
          livingBg.fillRoundedRect(-btnWidth / 2 - 4, -btnHeight / 2 - 4, btnWidth + 8, btnHeight + 8, 12);
          livingBg.fillStyle(0x00ff88, 0.3);
          livingBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
          livingBg.lineStyle(2.5, 0x00ff88, 1);
          livingBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
          livingText.setColor('#ffffff');

          // Update NON-LIVING button to unselected state
          nonLivingBg.clear();
          nonLivingBg.fillStyle(0x0a1828, 0.9);
          nonLivingBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
          nonLivingBg.lineStyle(2, 0x6699cc, 0.5);
          nonLivingBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
          nonLivingText.setColor('#6699cc');
        });

        nonLivingBtn.on('pointerdown', () => {
          this.classifiedCells.set(specimenId, 'non-living');
          this.updateClassifyObjective();

          // Check if correct
          const nowCorrect = !placed.specimen.isLiving;
          const newStatusColor = nowCorrect ? 0x00ff88 : 0xff6b6b;
          const newStatusText = nowCorrect ? '✓ CORRECT' : '✗ INCORRECT';

          // Update status badge
          statusBadge.clear();
          statusBadge.fillStyle(newStatusColor, 0.15);
          statusBadge.fillRoundedRect(-45, classifyY - 18, 90, 16, 8);
          classifyStatus.setText(newStatusText);
          classifyStatus.setColor(nowCorrect ? '#00ff88' : '#ff6b6b');

          // Update card border glow
          cardBg.clear();
          cardBg.fillStyle(newStatusColor, 0.12);
          cardBg.fillRoundedRect(-cardWidth / 2 - 6, -cardHeight / 2 - 6, cardWidth + 12, cardHeight + 12, 16);
          cardBg.fillStyle(newStatusColor, 0.06);
          cardBg.fillRoundedRect(-cardWidth / 2 - 10, -cardHeight / 2 - 10, cardWidth + 20, cardHeight + 20, 18);
          cardBg.fillStyle(0x040d18, 0.97);
          cardBg.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 12);
          cardBg.fillStyle(0x00ffaa, 0.04);
          cardBg.fillRoundedRect(-cardWidth / 2 + 1, -cardHeight / 2 + 1, cardWidth - 2, 40, { tl: 12, tr: 12, bl: 0, br: 0 });
          cardBg.lineStyle(2, newStatusColor, 0.7);
          cardBg.strokeRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 12);

          // Update NON-LIVING button to selected state
          nonLivingBg.clear();
          nonLivingBg.fillStyle(0x6699cc, 0.08);
          nonLivingBg.fillRoundedRect(-btnWidth / 2 - 4, -btnHeight / 2 - 4, btnWidth + 8, btnHeight + 8, 12);
          nonLivingBg.fillStyle(0x6699cc, 0.3);
          nonLivingBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
          nonLivingBg.lineStyle(2.5, 0x6699cc, 1);
          nonLivingBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
          nonLivingText.setColor('#ffffff');

          // Update LIVING button to unselected state
          livingBg.clear();
          livingBg.fillStyle(0x0a2818, 0.9);
          livingBg.fillRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
          livingBg.lineStyle(2, 0x00ff88, 0.5);
          livingBg.strokeRoundedRect(-btnWidth / 2, -btnHeight / 2, btnWidth, btnHeight, 8);
          livingText.setColor('#00ff88');
        });

        // Add all elements to card
        card.add([
          cardBg, specNumBg, specNum, nameText, typeBadge, typeText,
          dishBg, cellViz, evidenceLabel, separator, statusBadge, classifyStatus,
          livingBtn, nonLivingBtn
        ]);
        contentContainer.add(card);
        row++;
      });

      // ─────────────────────────────────────────────────────────────
      // SCROLLING SUPPORT
      // ─────────────────────────────────────────────────────────────
      const totalRows = Math.ceil(row / cardsPerRow);
      const totalContentHeight = totalRows * (cardHeight + 15);
      const maxScroll = Math.max(0, totalContentHeight - contentHeight);

      if (maxScroll > 0) {
        // Create scroll mask
        const maskShape = this.make.graphics({ x: 0, y: 0 });
        maskShape.fillStyle(0xffffff);
        maskShape.fillRect(
          width / 2 - panelWidth / 2 + 10,
          height / 2 + contentY,
          panelWidth - 20,
          contentHeight
        );
        const mask = maskShape.createGeometryMask();
        contentContainer.setMask(mask);

        // Scroll state
        let scrollY = 0;
        let isDragging = false;
        let dragStartY = 0;
        let scrollStartY = 0;

        // Create interactive scroll area
        const scrollArea = this.add.graphics();
        scrollArea.fillStyle(0x000000, 0.001); // Nearly invisible but interactive
        scrollArea.fillRect(-panelWidth / 2 + 10, 0, panelWidth - 20, contentHeight);
        contentContainer.add(scrollArea);
        scrollArea.setInteractive(
          new Phaser.Geom.Rectangle(-panelWidth / 2 + 10, 0, panelWidth - 20, contentHeight),
          Phaser.Geom.Rectangle.Contains
        );

        // Mouse wheel scrolling
        this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: unknown, _deltaX: number, deltaY: number) => {
          if (!this.journalOverlay) return;

          scrollY = Phaser.Math.Clamp(scrollY + deltaY * 0.5, 0, maxScroll);
          contentContainer.y = contentY - scrollY;
        });

        // Drag scrolling
        scrollArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
          isDragging = true;
          dragStartY = pointer.y;
          scrollStartY = scrollY;
        });

        this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
          if (!isDragging || !this.journalOverlay) return;

          const deltaY = dragStartY - pointer.y;
          scrollY = Phaser.Math.Clamp(scrollStartY + deltaY, 0, maxScroll);
          contentContainer.y = contentY - scrollY;
        });

        this.input.on('pointerup', () => {
          isDragging = false;
        });

        // Scroll indicator (thin bar on the right)
        const scrollTrackHeight = contentHeight - 20;
        const scrollThumbHeight = Math.max(30, (contentHeight / totalContentHeight) * scrollTrackHeight);

        const scrollTrack = this.add.graphics();
        scrollTrack.fillStyle(0x00ffaa, 0.1);
        scrollTrack.fillRoundedRect(panelWidth / 2 - 20, 10, 6, scrollTrackHeight, 3);

        const scrollThumb = this.add.graphics();
        scrollThumb.fillStyle(0x00ffaa, 0.4);
        scrollThumb.fillRoundedRect(panelWidth / 2 - 20, 10, 6, scrollThumbHeight, 3);

        contentContainer.add([scrollTrack, scrollThumb]);

        // Update scroll thumb position on scroll
        const updateScrollThumb = () => {
          const thumbY = 10 + (scrollY / maxScroll) * (scrollTrackHeight - scrollThumbHeight);
          scrollThumb.clear();
          scrollThumb.fillStyle(0x00ffaa, 0.4);
          scrollThumb.fillRoundedRect(panelWidth / 2 - 20, thumbY, 6, scrollThumbHeight, 3);
        };

        // Attach update to scroll events
        this.input.on('wheel', updateScrollThumb);
        this.input.on('pointermove', () => {
          if (isDragging) updateScrollThumb();
        });
      }
    }

    // Close button
    const closeBtn = this.add.container(panelWidth / 2 - 25, -panelHeight / 2 + 25);
    const closeBtnBg = this.add.graphics();
    closeBtnBg.fillStyle(0x0a2030, 0.9);
    closeBtnBg.fillCircle(0, 0, 15);
    closeBtnBg.lineStyle(1, 0x00ffaa, 0.5);
    closeBtnBg.strokeCircle(0, 0, 15);

    const closeX = this.add.text(0, 0, '✕', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      color: '#00ffaa',
    }).setOrigin(0.5);

    closeBtn.add([closeBtnBg, closeX]);
    closeBtn.setSize(30, 30);
    closeBtn.setInteractive({ useHandCursor: true });

    closeBtn.on('pointerover', () => {
      closeBtnBg.clear();
      closeBtnBg.fillStyle(0x00ffaa, 0.2);
      closeBtnBg.fillCircle(0, 0, 15);
      closeBtnBg.lineStyle(1, 0x00ffaa, 0.8);
      closeBtnBg.strokeCircle(0, 0, 15);
    });
    closeBtn.on('pointerout', () => {
      closeBtnBg.clear();
      closeBtnBg.fillStyle(0x0a2030, 0.9);
      closeBtnBg.fillCircle(0, 0, 15);
      closeBtnBg.lineStyle(1, 0x00ffaa, 0.5);
      closeBtnBg.strokeCircle(0, 0, 15);
    });
    closeBtn.on('pointerdown', () => this.closeJournal());

    // Add floating particles
    const particles: Phaser.GameObjects.Graphics[] = [];
    for (let i = 0; i < 15; i++) {
      const particle = this.add.graphics();
      const px = (Math.random() - 0.5) * panelWidth * 0.8;
      const py = (Math.random() - 0.5) * panelHeight * 0.8;
      particle.fillStyle(0x00ffaa, 0.15 + Math.random() * 0.2);
      particle.fillCircle(px, py, 1 + Math.random() * 2);
      particles.push(particle);

      // Animate particles
      this.tweens.add({
        targets: particle,
        y: py + (Math.random() - 0.5) * 40,
        alpha: { from: 0.3, to: 0.1 },
        duration: 2000 + Math.random() * 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Assemble overlay
    this.journalOverlay.add([overlay, glow, panel, dnaHelix, headerBg, title, subtitle, divider, contentContainer, closeBtn, ...particles]);

    // Entrance animation
    this.journalOverlay.setScale(0.9);
    this.journalOverlay.setAlpha(0);
    this.tweens.add({
      targets: this.journalOverlay,
      scale: 1,
      alpha: 1,
      duration: 250,
      ease: 'Back.easeOut',
    });

    // Close on overlay click (outside panel)
    overlay.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const localX = pointer.x - width / 2;
      const localY = pointer.y - height / 2;
      if (Math.abs(localX) > panelWidth / 2 || Math.abs(localY) > panelHeight / 2) {
        this.closeJournal();
      }
    });

    // Keyboard close
    this.input.keyboard?.once('keydown-ESC', () => this.closeJournal());
    this.input.keyboard?.once('keydown-J', () => this.closeJournal());
  }

  /**
   * Close journal overlay
   */
  private closeJournal(): void {
    if (!this.journalOverlay) return;

    this.tweens.add({
      targets: this.journalOverlay,
      scale: 0.9,
      alpha: 0,
      duration: 150,
      ease: 'Power2',
      onComplete: () => {
        this.journalOverlay?.destroy();
        this.journalOverlay = undefined;
      },
    });
  }

  /**
   * Pause game
   */
  private pauseGame(): void {
    this.events.emit('game:pause');
    this.scene.pause();
  }

  /**
   * Exit level and return to menu
   */
  private exitLevel(): void {
    this.cleanup();
    this.scene.start('MenuScene');
  }

  /**
   * Catalog a discovered cell
   */
  public catalogCell(specimenId: string): void {
    const placed = this.placedSpecimens.get(specimenId);
    if (placed && placed.scanned && !placed.cataloged) {
      placed.cataloged = true;
      this.catalogedCells.add(specimenId);
      this.updateObjective('catalog_cells', this.catalogedCells.size);
      this.events.emit('cell:cataloged', { specimenId, templateId: placed.templateId });
    }
  }

  /**
   * Get current level progress
   */
  public getProgress(): { objectives: ObjectiveProgress[]; discoveries: number; evidence: number } {
    return {
      objectives: this.objectives,
      discoveries: this.discoveredCells.size,
      evidence: this.collectedEvidence.length,
    };
  }

  /**
   * Clean up resources
   */
  private cleanup(): void {
    this.microscopeSystem.destroy();
    this.scannerSystem.destroy();
    this.animationSystem.destroy();
    this.microscopeHUD.destroy();
    this.scannerPanel.destroy();
    this.evidenceCards.destroy();

    this.events.off('scanner:complete', this.handleScanComplete, this);
    this.events.off('evidence:collected', this.handleEvidenceCollected, this);
  }
}
